import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import type { ShippingPlan_ShipFragment } from '../../graphql';
import { mapFlatmap, mapMap } from '../../lib/util';
import type {
  CachedBerthPrediction,
  CachedBerthPredictionInput,
} from '../core/BerthPrediction';
import {
  getBerthPredictionInputs,
  berthPredictionResultIntoMap,
  cachedFetchBerthPredictions,
} from '../core/Core';
import type { Leg, WaitEvent, TimeData } from '../core/TimeData';
import { isTimeData } from '../core/TimeData';
import { stringToNumOpt } from '../core/util';

const useFetchCachedBerthPredictions = (
  inputs: CachedBerthPredictionInput[],
  opts: { skip: boolean },
): {
  data: (CachedBerthPrediction | null)[];
  isFetching: boolean;
  error: string | undefined;
} => {
  const client = useApolloClient();
  const [berthPredictions, setBerthPredictions] = useState<
    (CachedBerthPrediction | null)[]
  >([]);

  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (opts.skip) {
      return;
    }
    if (inputs.length === 0) {
      setBerthPredictions([]);
      return;
    }
    setIsFetching(true);
    cachedFetchBerthPredictions(client, inputs)
      .then((results) => {
        setBerthPredictions(results);
        setIsFetching(false);
      })
      .catch((e) => {
        setError(e.message);
        setIsFetching(false);
      });
  }, [client, inputs, opts.skip]);

  return {
    data: berthPredictions,
    isFetching,
    error,
  };
};

export function useBerthPrediction(
  isBerthPredictionAvailable: boolean,
  vessel: ShippingPlan_ShipFragment | undefined,
  shownEventsAndLegs: (Leg | WaitEvent | TimeData)[],
) {
  const berthPredictionInputs = useMemo(() => {
    return getBerthPredictionInputs(
      shownEventsAndLegs.filter(isTimeData),
      vessel?.name ?? null,
      stringToNumOpt(vessel?.imo) ?? null,
      isBerthPredictionAvailable,
    );
  }, [shownEventsAndLegs, vessel, isBerthPredictionAvailable]);

  const berthPredictionInputArray = useMemo(
    () => mapFlatmap(berthPredictionInputs, (value) => mapMap(value, (v) => v)),
    [berthPredictionInputs],
  );

  const { data: berthPredictionData } = useFetchCachedBerthPredictions(
    berthPredictionInputArray,
    {
      skip: !berthPredictionInputArray.length,
    },
  );

  return useMemo(
    () =>
      berthPredictionResultIntoMap(berthPredictionInputs, berthPredictionData),
    [berthPredictionData, berthPredictionInputs],
  );
}
